import type { AccountOption } from 'src/components/AccountsAutocomplete';
import { Apollo } from 'src/gql/generated';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';

type WorkflowPermissionAddCandidate = Pick<
  WorkflowPermissionSubset,
  'account' | 'id' | 'role' | 'timeliness'
>;

export function getWorkflowPermissionAddPlan({
  accounts,
  permissions,
  timeliness,
  workflowVariant,
}: {
  accounts: AccountOption[];
  permissions: WorkflowPermissionAddCandidate[];
  timeliness: Apollo.WorkflowPermissionTimeliness;
  workflowVariant: Apollo.WorkflowVariant;
}) {
  const plan = accounts.reduce<{
    accountsToCreate: AccountOption[];
    permissionIdSetToActivate: Set<UUID>;
  }>(
    (acc, account) => {
      const permission = getQueuedPermissionToActivate({
        account,
        permissions,
        timeliness,
        workflowVariant,
      });
      if (permission) {
        acc.permissionIdSetToActivate.add(permission.id);
        return acc;
      }

      acc.accountsToCreate.push(account);
      return acc;
    },
    { accountsToCreate: [], permissionIdSetToActivate: new Set() }
  );

  return {
    accountsToCreate: plan.accountsToCreate,
    permissionIdsToActivate: [...plan.permissionIdSetToActivate],
  };
}

export function getIsAccountDisabledForWorkflowPermissionAdd({
  account,
  permissions,
  timeliness,
}: {
  account: AccountOption;
  permissions: WorkflowPermissionAddCandidate[];
  timeliness: Apollo.WorkflowPermissionTimeliness;
}) {
  return Boolean(getMatchingRolelessPermissionForAccount({ account, permissions, timeliness }));
}

function getQueuedPermissionToActivate({
  account,
  permissions,
  timeliness,
  workflowVariant,
}: {
  account: AccountOption;
  permissions: WorkflowPermissionAddCandidate[];
  timeliness: Apollo.WorkflowPermissionTimeliness;
  workflowVariant: Apollo.WorkflowVariant;
}) {
  if (timeliness !== Apollo.WorkflowPermissionTimeliness.Active) return null;
  /**
   * Queued permissions are never activated/promoted for template workflows.
   * Moving from Queued => Active only happens on launch and during workflow phase changes.
   */
  if (workflowVariant === Apollo.WorkflowVariant.Template) return null;

  return (
    getMatchingRolelessPermissionForAccount({
      account,
      permissions,
      timeliness: Apollo.WorkflowPermissionTimeliness.Queued,
    }) ?? null
  );
}

function getMatchingRolelessPermissionForAccount({
  account,
  permissions,
  timeliness,
}: {
  account: AccountOption;
  permissions: WorkflowPermissionAddCandidate[];
  timeliness: Apollo.WorkflowPermissionTimeliness;
}) {
  return (
    permissions.find(permission => {
      if (permission.role || permission.timeliness !== timeliness) return false;
      return getAccountMatchesPermission(account, permission);
    }) ?? null
  );
}

function getAccountMatchesPermission(
  account: AccountOption,
  permission: WorkflowPermissionAddCandidate
) {
  switch (account.__typename) {
    case 'NewUser':
      return (
        permission.account?.__typename === 'User' &&
        permission.account.email.toLowerCase() === account.email.toLowerCase()
      );
    case 'Org':
    case 'User':
      return permission.account?.id === account.id;
  }
}
